import React, { useState } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Card,
  Button,
  Stack,
  CircularProgress,
  Alert,
  Radio,
  RadioGroup
} from '@mui/joy';
import {
  Buildings as BusinessIcon,
  MapPin as LocationIcon,
  Phone as PhoneIcon,
  Globe as WebsiteIcon,
  CheckCircle as CheckIcon
} from '@phosphor-icons/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';

interface GBPLocation {
  name: string;
  title: string;
  storefrontAddress?: {
    addressLines?: string[];
    locality?: string;
    administrativeArea?: string;
    postalCode?: string;
  };
  phoneNumbers?: {
    primaryPhone?: string;
  };
  websiteUri?: string;
  metadata?: {
    placeId?: string;
  };
}

interface GBPLocationSelectModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const GBPLocationSelectModal: React.FC<GBPLocationSelectModalProps> = ({
  open,
  onClose,
  onSuccess
}) => {
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Fetch pending locations from session
  const { data: locationsData, isLoading, error } = useQuery({
    queryKey: ['gbp-pending-locations'],
    queryFn: async () => {
      const response = await api.get<{ locations: GBPLocation[]; count: number }>(
        '/api/marketing/gbp-profiles/pending-locations/'
      );
      return response.data;
    },
    enabled: open,
    retry: false
  });

  // Select location mutation
  const selectMutation = useMutation({
    mutationFn: async (locationName: string) => {
      return api.post('/api/marketing/gbp-profiles/select-location/', {
        location_id: locationName
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gbp-profiles'] });
      onSuccess();
      onClose();
    }
  });

  const formatAddress = (location: GBPLocation): string => {
    const addr = location.storefrontAddress;
    if (!addr) return 'Address not available';

    const parts: string[] = [];
    if (addr.addressLines?.length) {
      parts.push(addr.addressLines.join(', '));
    }
    if (addr.locality) parts.push(addr.locality);
    if (addr.administrativeArea) parts.push(addr.administrativeArea);
    if (addr.postalCode) parts.push(addr.postalCode);

    return parts.join(', ') || 'Address not available';
  };

  const handleSelect = () => {
    if (selectedLocation) {
      selectMutation.mutate(selectedLocation);
    }
  };

  const locations = locationsData?.locations || [];

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog
        size="lg"
        sx={{
          width: '90%',
          maxWidth: 600,
          maxHeight: '85vh',
          overflow: 'hidden'
        }}
      >
        <ModalClose />
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BusinessIcon />
            Select Google Business Profile
          </Box>
        </DialogTitle>
        <DialogContent sx={{ overflow: 'auto' }}>
          <Typography level="body-md" sx={{ mb: 3, color: 'text.secondary' }}>
            Your Google account has access to multiple business profiles.
            Please select the one you want to connect to ApexOS.
          </Typography>

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert color="danger">
              Failed to load business profiles. Please try connecting again.
            </Alert>
          ) : locations.length === 0 ? (
            <Alert color="warning">
              No business profiles found. Please make sure your Google account has access to at least one Google Business Profile.
            </Alert>
          ) : (
            <>
              <RadioGroup
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
              >
                <Stack spacing={2}>
                  {locations.map((location) => (
                    <Card
                      key={location.name}
                      variant={selectedLocation === location.name ? 'soft' : 'outlined'}
                      color={selectedLocation === location.name ? 'primary' : 'neutral'}
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        border: selectedLocation === location.name ? '2px solid' : '1px solid',
                        borderColor: selectedLocation === location.name ? 'primary.500' : 'neutral.outlinedBorder',
                        '&:hover': {
                          borderColor: 'primary.400',
                          bgcolor: selectedLocation === location.name ? undefined : 'background.level1'
                        }
                      }}
                      onClick={() => setSelectedLocation(location.name)}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, p: 1 }}>
                        <Radio
                          value={location.name}
                          sx={{ mt: 0.5 }}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography level="title-md">
                            {location.title}
                          </Typography>

                          <Stack spacing={0.5} sx={{ mt: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LocationIcon size={16} color="var(--joy-palette-text-tertiary)" />
                              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                                {formatAddress(location)}
                              </Typography>
                            </Box>

                            {location.phoneNumbers?.primaryPhone && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PhoneIcon size={16} color="var(--joy-palette-text-tertiary)" />
                                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                                  {location.phoneNumbers.primaryPhone}
                                </Typography>
                              </Box>
                            )}

                            {location.websiteUri && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <WebsiteIcon size={16} color="var(--joy-palette-text-tertiary)" />
                                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                                  {location.websiteUri}
                                </Typography>
                              </Box>
                            )}
                          </Stack>
                        </Box>

                        {selectedLocation === location.name && (
                          <CheckIcon color="var(--joy-palette-primary-600)" />
                        )}
                      </Box>
                    </Card>
                  ))}
                </Stack>
              </RadioGroup>

              {selectMutation.isError && (
                <Alert color="danger" sx={{ mt: 2 }}>
                  Failed to connect the selected profile. Please try again.
                </Alert>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                <Button
                  variant="plain"
                  color="neutral"
                  onClick={onClose}
                  disabled={selectMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  variant="solid"
                  color="primary"
                  onClick={handleSelect}
                  disabled={!selectedLocation || selectMutation.isPending}
                  loading={selectMutation.isPending}
                  startDecorator={<CheckIcon />}
                >
                  Connect Selected Profile
                </Button>
              </Box>
            </>
          )}
        </DialogContent>
      </ModalDialog>
    </Modal>
  );
};
